import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '../config/api';
import { clearPermissionsCache } from '../utils/permissions';

// Hook for managing user permissions
export const usePermissions = () => {
  const [permissions, setPermissions] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user permissions
  const { data, isLoading: queryLoading, refetch } = useQuery({
    queryKey: ['userPermissions'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      if (!token) return null;

      const response = await fetch(`${API_BASE_URL}/api/user-permissions/me/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch permissions');
      }

      const data = await response.json();
      return data.data;
    },
    enabled: !!localStorage.getItem('token'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime)
  });

  // Update loading state and handle data
  useEffect(() => {
    setIsLoading(queryLoading);
  }, [queryLoading]);

  // Handle data when loaded
  useEffect(() => {
    if (data?.permissions) {
      console.log('🔐 Permissions loaded:', data);
      setPermissions(data.permissions);
      console.log('🔐 Permissions set:', data.permissions);
    }
  }, [data]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permissionCode) => {
    if (!permissions || Object.keys(permissions).length === 0) {
      console.log('🔐 No permissions loaded for:', permissionCode);
      return false;
    }
    
    // Flatten permissions from all categories
    const allPermissions = Object.values(permissions).flat();
    
    // Find the permission
    const permission = allPermissions.find(p => p.code === permissionCode);
    const hasPermission = permission ? permission.granted : false;
    
    console.log(`🔐 Permission check: ${permissionCode} = ${hasPermission}`);
    return hasPermission;
  }, [permissions]);

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback((permissionCodes) => {
    if (!permissions || Object.keys(permissions).length === 0) return false;
    
    const allPermissions = Object.values(permissions).flat();
    
    return permissionCodes.some(code => {
      const permission = allPermissions.find(p => p.code === code);
      return permission ? permission.granted : false;
    });
  }, [permissions]);

  // Check if user can access a navigation item
  const canAccessNavigationItem = useCallback((itemKey) => {
    const NAVIGATION_PERMISSIONS = {
      'customers': 'customers.read',
      'class-of-trade': 'customers.manage_cot',
      'products': 'products.read',
      'contracts': 'contracts.read',
      'gpos': 'customers.read',
      'sales-data': 'reports.view',
      'chargebacks': 'chargebacks.read',
      'upload': 'edi.upload',
      'edi': 'edi.read',
      'batches': 'batches.read',
      'reports': 'reports.view',
      'amp': 'amp.view',
      'admin': 'users.read',
      'user-management': 'users.read',
      'role-management': 'users.read',
      'user-permissions': 'users.manage_permissions',
      'test-data': 'system.settings',
      'system-settings': 'system.settings'
    };

    const requiredPermission = NAVIGATION_PERMISSIONS[itemKey];
    if (!requiredPermission) return true; // No permission required
    
    return hasPermission(requiredPermission);
  }, [hasPermission]);

  // Refresh permissions
  const refreshPermissions = useCallback(() => {
    clearPermissionsCache();
    refetch();
  }, [refetch]);

  // Get permission summary
  const getPermissionSummary = useCallback(() => {
    if (!permissions || Object.keys(permissions).length === 0) {
      return { total: 0, granted: 0, revoked: 0, categories: [] };
    }

    const allPermissions = Object.values(permissions).flat();
    
    return {
      total: allPermissions.length,
      granted: allPermissions.filter(p => p.granted).length,
      revoked: allPermissions.filter(p => !p.granted).length,
      categories: Object.keys(permissions)
    };
  }, [permissions]);

  return {
    permissions,
    isLoading,
    hasPermission,
    hasAnyPermission,
    canAccessNavigationItem,
    refreshPermissions,
    getPermissionSummary
  };
};
